import { useState, useEffect, useCallback, useMemo } from 'react';

const MAX_SUGGESTIONS = 5;
const STORAGE_KEY_PREFIX = 'predictions_';

type LearnedWords = Record<string, number>;

function getLearnedWords(lang: string): LearnedWords {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + lang);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveLearnedWords(lang: string, learned: LearnedWords) {
  localStorage.setItem(STORAGE_KEY_PREFIX + lang, JSON.stringify(learned));
}

const dictionaryCache: Record<string, string[]> = {};

async function loadDictionary(lang: string): Promise<string[]> {
  if (dictionaryCache[lang]) return dictionaryCache[lang];

  try {
    const langKey = lang.split('-')[0];
    let dict: string[];
    switch (langKey) {
      case 'it':
        dict = (await import('@data/dictionaries/it.json')).default;
        break;
      case 'en':
        dict = (await import('@data/dictionaries/en.json')).default;
        break;
      case 'fr':
        dict = (await import('@data/dictionaries/fr.json')).default;
        break;
      case 'es':
        dict = (await import('@data/dictionaries/es.json')).default;
        break;
      case 'de':
        dict = (await import('@data/dictionaries/de.json')).default;
        break;
      default:
        dict = (await import('@data/dictionaries/it.json')).default;
    }
    dictionaryCache[lang] = dict;
    return dict;
  } catch {
    return [];
  }
}

export function usePrediction(text: string, lang: string) {
  const [dictionary, setDictionary] = useState<string[]>([]);
  const [learned, setLearned] = useState<LearnedWords>(() =>
    getLearnedWords(lang)
  );

  useEffect(() => {
    loadDictionary(lang).then(setDictionary);
    setLearned(getLearnedWords(lang));
  }, [lang]);

  const prefix = useMemo(() => {
    const trimmed = text.replace(/\s+$/, '');
    if (text.endsWith(' ') || trimmed.length === 0) return '';
    const parts = trimmed.split(/\s+/);
    return parts[parts.length - 1].toLowerCase();
  }, [text]);

  const suggestions = useMemo(() => {
    if (dictionary.length === 0) return [];

    if (!prefix) {
      // No prefix: show most-used learned words, then top dictionary words
      const learnedSorted = Object.entries(learned)
        .sort((a, b) => b[1] - a[1])
        .map(([word]) => word);

      const result = learnedSorted.slice(0, MAX_SUGGESTIONS);
      if (result.length < MAX_SUGGESTIONS) {
        for (const word of dictionary) {
          if (result.length >= MAX_SUGGESTIONS) break;
          if (!result.includes(word)) result.push(word);
        }
      }
      return result;
    }

    // With prefix: filter matches, prioritize learned words
    const learnedMatches = Object.entries(learned)
      .filter(([word]) => word.startsWith(prefix) && word !== prefix)
      .sort((a, b) => b[1] - a[1])
      .map(([word]) => word);

    const dictMatches = dictionary.filter(
      (word) =>
        word.startsWith(prefix) &&
        word !== prefix &&
        !learnedMatches.includes(word)
    );

    return [...learnedMatches, ...dictMatches].slice(0, MAX_SUGGESTIONS);
  }, [prefix, dictionary, learned]);

  const selectSuggestion = useCallback(
    (word: string): string => {
      // Bump learned frequency
      const updated = { ...learned, [word]: (learned[word] || 0) + 1 };
      setLearned(updated);
      saveLearnedWords(lang, updated);

      // Replace current partial word with the selected word + space
      if (!prefix) return text + word + ' ';
      const lastSpaceIdx = text.lastIndexOf(' ');
      const before = lastSpaceIdx >= 0 ? text.slice(0, lastSpaceIdx + 1) : '';
      return before + word + ' ';
    },
    [text, prefix, lang, learned]
  );

  return { suggestions, selectSuggestion };
}
